import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBotGuard } from "@/components/BotTrap";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, verificationRequiredFrom } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { trap, guard } = useBotGuard();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [allowView, setAllowView] = useState(false);
  const [allowModify, setAllowModify] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const data = await register(
        name.trim(),
        email.trim(),
        password,
        { allowView, allowModify },
        guard(),
      );
      toast.success("Account created — check your email for the code");
      nav("/verify-email", {
        replace: true,
        state: {
          email: data?.email || email.trim().toLowerCase(),
          retryAfter: data?.retry_after,
          justRegistered: true,
        },
      });
    } catch (e) {
      // Re-registering an interrupted signup resumes verification instead of
      // dead-ending on "email already registered".
      const verification = verificationRequiredFrom(e);
      if (verification) {
        toast.info(verification.message);
        nav("/verify-email", {
          replace: true,
          state: {
            email: verification.email || email.trim().toLowerCase(),
            retryAfter: verification.retry_after,
          },
        });
        return;
      }
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-[var(--border)] bg-white">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[var(--fg)] text-white grid place-items-center font-bold">IF</div>
          <span className="font-semibold tracking-tight">InvoiceForge</span>
        </Link>
        <div>
          <div className="overline mb-4">Get started</div>
          <h1 className="text-5xl leading-none tracking-tight font-medium">
            One account. <br /><span className="font-serif italic">Every</span> document.
          </h1>
          <p className="mt-6 text-gray-600 max-w-md">Proforma, sales, timesheet, subscription, milestone, commercial, credit/debit notes, and receipts — all with 5 template designs.</p>
        </div>
        <div className="overline">INR & USD supported · GST-ready</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-sm">
          {trap}
          <div className="overline mb-3">Create account</div>
          <h2 className="text-3xl font-medium tracking-tight mb-8">Let&rsquo;s get you set up.</h2>
          <label className="block mb-4">
            <div className="text-xs text-gray-600 mb-1.5">Your name</div>
            <input
              required value={name} onChange={(e) => setName(e.target.value)}
              className="w-full h-11 px-3 border border-[var(--border)] rounded-md bg-white focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none"
              data-testid="register-name"
            />
          </label>
          <label className="block mb-4">
            <div className="text-xs text-gray-600 mb-1.5">Email</div>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 border border-[var(--border)] rounded-md bg-white focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none"
              data-testid="register-email"
            />
          </label>
          <label className="block mb-6">
            <div className="text-xs text-gray-600 mb-1.5">Password <span className="text-gray-400">· min 6 chars</span></div>
            <input
              type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-3 border border-[var(--border)] rounded-md bg-white focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 outline-none"
              data-testid="register-password"
            />
          </label>

          <div className="mb-6 space-y-3 p-4 rounded-md border border-[var(--border)] bg-gray-50/60">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
                required className="mt-1" data-testid="register-accept-terms" />
              <span className="text-xs text-gray-700 leading-relaxed">
                I accept the <Link to="/terms" target="_blank" className="underline underline-offset-2 text-[var(--fg)] font-medium">Terms &amp; Conditions</Link>,
                including cookie / cache use and the disclaimer that <strong>InvoiceForge / PrismGrim is not
                responsible for hacking, data leaks or related security incidents</strong>.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={allowView} onChange={(e) => setAllowView(e.target.checked)}
                className="mt-1" data-testid="register-allow-view" />
              <span className="text-xs text-gray-700 leading-relaxed">
                <strong>Data visibility</strong>: allow a PrismGrim administrator to view my account data.
                <span className="text-gray-500"> Optional — toggle later in Company Profile.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={allowModify} onChange={(e) => setAllowModify(e.target.checked)}
                className="mt-1" data-testid="register-allow-modify" />
              <span className="text-xs text-gray-700 leading-relaxed">
                <strong>Data modification</strong>: allow a PrismGrim administrator to update my profile on request.
                <span className="text-gray-500"> Optional.</span>
              </span>
            </label>
          </div>

          {err && <div className="mb-4 text-sm text-red-600" data-testid="register-error">{err}</div>}
          <button className="w-full btn-primary" disabled={busy || !acceptTerms} data-testid="register-submit">
            {busy ? "Creating…" : "Create account"}
          </button>
          <p className="mt-3 text-[11px] leading-relaxed text-gray-500">
            We&rsquo;ll email a 6-digit verification code. The dashboard unlocks only after it&rsquo;s confirmed.
          </p>
          <div className="mt-6 text-sm text-gray-600">
            Already have one? <Link to="/login" className="underline underline-offset-4 text-[var(--fg)]" data-testid="link-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
